import { useState, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { usePdfActions } from "../../hooks/usePdfActions"
import { FiUploadCloud, FiCheckCircle, FiCpu, FiShield, FiZap, FiArrowRight, FiFileText, FiScissors, FiMonitor, FiActivity, FiLoader, FiSearch, FiAlertCircle, FiX, FiLayers, FiBarChart2, FiAward, FiMessageSquare, FiCommand, FiBook, FiClock, FiTarget, FiUser, FiTrendingUp, FiLayout } from "react-icons/fi"
import { useNotification } from "../../context/NotificationContext"

export default function FileUploader() {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null)
  const [analysisResult, setAnalysisResult] = useState<string>("")
  const [loading, setLoading] = useState<boolean>(false)
  const { parsePdf } = usePdfActions();
  const { showNotification } = useNotification();
  const analyzerRef = useRef<HTMLDivElement>(null);

  const scrollToAnalyzer = () => {
    analyzerRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  async function uploadPDF(file: File): Promise<{ result?: string; error?: string }> {
    const token = localStorage.getItem("token");
    if (!token) {
      return { error: "Please log in to use the AI analyzer." }
    }
    try {
      const formData = new FormData()
      formData.append("file", file)
      const data = await parsePdf(formData);
      return { result: data.result }
    } catch (error: any) {
      console.error("PDF upload error:", error)
      return { error: error.response?.data?.message || error.message || "An unexpected error occurred while parsing the PDF." }
    }
  }

  const handleAnalyze = async () => {
    if (!file) {
      showNotification("Please select a PDF file before uploading.", "error")
      return
    }

    setLoading(true)
    setAnalysisResult("")

    const { result, error: uploadError } = await uploadPDF(file)

    if (uploadError) {
      showNotification(uploadError, "error")
    } else if (result) {
      setAnalysisResult(result)
      showNotification("Analysis completed successfully!", "success")
    } else {
      showNotification("No content received or unknown error occurred.", "error")
    }

    setLoading(false)
  }

  const parseAndHighlightText = (text: string) => {
    return text.split('\n').map((line, index) => {
      const lowerLine = line.toLowerCase();
      if (lowerLine.includes("interview questions") || lowerLine.includes("summary") || lowerLine.includes("feedback") || lowerLine.includes("skills")) {
        return (
          <h4 key={`heading-${index}`} className="fw-900 text-dark mt-4 mb-3 d-flex align-items-center gap-2">
            <FiCheckCircle className="text-primary" /> {line.trim().replace(/\*/g, '')}
          </h4>
        );
      } else if (line.trim() === '') {
        return <br key={`break-${index}`} />;
      }
      return <p key={`line-${index}`} className="mb-2 text-muted fw-medium">{line.trim().replace(/\*/g, '')}</p>;
    });
  };

  return (
    <div className="min-vh-100 hero-bg overflow-hidden position-relative">
      {/* Background Blobs */}
      <div className="blob animate-morph bg-primary" style={{ top: '-10%', left: '-5%', width: '800px', height: '800px', opacity: 0.15 }}></div>
      <div className="blob animate-morph bg-secondary" style={{ bottom: '10%', right: '-5%', width: '700px', height: '700px', opacity: 0.1, animationDelay: '-2s' }}></div>

      <main className="position-relative z-10">
        {/* Hero Section */}
        <div className="container pt-5 mt-lg-5">
          <div className="row align-items-center min-vh-lg-100 g-5 py-5 px-3 px-md-0">
            <div className="col-lg-7 animate-fade-in-up text-center text-lg-start">
              <div className="badge glass rounded-pill px-4 py-2 text-primary fw-900 mb-4 tracking-wider animate-pulse-glow shadow-sm mx-auto mx-lg-0">
                🚀 AI-POWERED DOCUMENT INTELLIGENCE
              </div>
              <h1 className="hero-title fw-900 mb-4 tracking-tight">
                Analyze <span className="curved-underline">Smarter.</span><br />
                <span className="text-gradient-premium">Succeed Faster.</span>
              </h1>
              <p className="lead text-muted mb-5 fs-4 fw-medium mx-auto mx-lg-0" style={{ maxWidth: '90%' }}>
                Unlock neural-powered insights from your professional documents. Our AI-driven engine provides precision feedback to help you optimize and succeed in seconds.
              </p>

              <div className="d-flex flex-wrap gap-4 justify-content-center justify-content-lg-start mb-5">
                <button
                  onClick={scrollToAnalyzer}
                  className="btn-premium px-5 py-3 fs-5"
                >
                  Start Analysis <FiArrowRight className="hover-scale" />
                </button>
                <div className="d-flex align-items-center gap-3 glass px-4 py-2 rounded-4 hover-scale cursor-pointer shadow-sm" onClick={() => navigate('/vision-studio')}>
                  <div className="bg-success bg-opacity-10 p-2 rounded-circle">
                    <FiMessageSquare className="text-success" />
                  </div>
                  <div className="lh-1">
                    <div className="small fw-900 text-dark">Vision Studio</div>
                    <div className="x-small text-success fw-bold">New Feature Live</div>
                  </div>
                </div>
              </div>

              {/* Trust Logos */}
              <div className="d-flex flex-wrap gap-4 justify-content-center justify-content-lg-start align-items-center opacity-75">
                <span className="small fw-900 text-muted uppercase tracking-widest me-2">Trusted By</span>
                <FiZap size={24} className="client-logo" />
                <FiActivity size={24} className="client-logo" />
                <FiCpu size={24} className="client-logo" />
                <FiShield size={24} className="client-logo" />
              </div>
            </div>

            <div className="col-lg-5 animate-fade-in-up mt-5 mt-lg-0" style={{ animationDelay: '0.2s' }}>
              <div className="position-relative">
                <div className="card-premium p-2 glass transform-perspective shadow-2xl overflow-hidden" style={{ borderRadius: '2.5rem' }}>
                  <img
                    src="https://images.unsplash.com/photo-1664575602276-acd073f104c1?auto=format&fit=crop&q=80&w=1000"
                    alt="AI Analysis Preview"
                    className="img-fluid rounded-5 shadow-sm w-100"
                    style={{ filter: 'brightness(0.95)', minHeight: '500px', objectFit: 'cover' }}
                  />
                  <div className="position-absolute top-50 start-50 translate-middle">
                    <div className="bg-white p-4 rounded-circle shadow-2xl animate-float border border-5 border-light">
                      <FiZap size={64} className="text-primary" />
                    </div>
                  </div>
                </div>

                {/* Floating Stats Card 1 */}
                <div className="stat-floating d-none d-md-flex" style={{ top: '10%', left: '-15%', animationDelay: '0s' }}>
                  <div className="icon-box mb-0 shadow-sm" style={{ width: '40px', height: '40px', borderRadius: '10px' }}><FiLayers /></div>
                  <div>
                    <div className="fw-900 text-dark small">Neural Parsing</div>
                    <div className="x-small text-muted fw-bold">Active Engine</div>
                  </div>
                </div>

                {/* Floating Stats Card 2 */}
                <div className="stat-floating d-none d-md-flex" style={{ bottom: '15%', right: '-10%', animationDelay: '-2.5s' }}>
                  <div className="icon-box mb-0 shadow-sm" style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'var(--premium-gradient)' }}><FiBarChart2 /></div>
                  <div>
                    <div className="fw-900 text-dark small">99% Accuracy</div>
                    <div className="x-small text-muted fw-bold">AI Verification</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Vision Studio Spotlight Section */}
        <section className="py-5 my-lg-5 overflow-hidden">
          <div className="container py-5">
            <div className="row align-items-center g-5">
              <div className="col-lg-6 order-2 order-lg-1">
                <div className="position-relative">
                  <div className="card-premium p-1 glass overflow-hidden shadow-2xl animate-scale-in" style={{ borderRadius: '2rem' }}>
                    <img
                      src="https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=1000"
                      className="img-fluid rounded-4 w-100"
                      alt="Vision Studio"
                      style={{ minHeight: '400px', objectFit: 'cover' }}
                    />
                    <div className="position-absolute inset-0 bg-primary bg-opacity-20 backdrop-blur-sm d-flex align-items-center justify-content-center opacity-0 hover-opacity-100 transition-all cursor-pointer" onClick={() => navigate('/vision-studio')}>
                      <div className="bg-white p-4 rounded-circle shadow-glow animate-pulse">
                        <FiMonitor size={48} className="text-primary" />
                      </div>
                    </div>
                  </div>
                  {/* Decorative Elements */}
                  <div className="position-absolute top-0 start-0 translate-middle-x mt-5 d-none d-md-block">
                    <div className="glass-dark p-4 rounded-4 shadow-xl animate-float">
                      <FiMessageSquare className="text-primary mb-2" size={32} />
                      <div className="fw-900 text-white small">Interactive Chat</div>
                    </div>
                  </div>
                  <div className="position-absolute bottom-0 end-0 translate-middle-x mb-5 d-none d-md-block">
                    <div className="glass-dark p-4 rounded-4 shadow-xl animate-float-delayed">
                      <FiCpu className="text-success mb-2" size={32} />
                      <div className="fw-900 text-white small">Neural Analysis</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-6 order-1 order-lg-2 animate-fade-in-up">
                <div className="badge glass-dark rounded-pill px-4 py-2 text-primary fw-900 mb-4 tracking-widest">
                  NEXT-GEN INTELLIGENCE
                </div>
                <h2 className="display-3 fw-900 text-dark mb-4 tracking-tight">
                  Meet the <span className="text-gradient-premium">Vision Studio</span>
                </h2>
                <p className="lead text-muted mb-4 fw-medium">
                  Don't just analyze documents—converse with them. Our new Vision Studio lets you upload any document and use interactive AI to extract data, generate summaries, and ask deep technical questions.
                </p>
                <ul className="list-unstyled mb-5 d-flex flex-column gap-3">
                  {[
                    { icon: <FiCheckCircle />, text: "Real-time AI Document Assistant" },
                    { icon: <FiCommand />, text: "One-click Semantic Extraction" },
                    { icon: <FiZap />, text: "Hyper-fast Contextual Reasoning" }
                  ].map((item, i) => (
                    <li key={i} className="d-flex align-items-center gap-3 fw-800 text-dark">
                      <span className="text-primary">{item.icon}</span> {item.text}
                    </li>
                  ))}
                </ul>
                <button onClick={() => navigate('/vision-studio')} className="btn-premium px-5 py-4 fs-4 shadow-2xl">
                  Explore Vision Studio <FiMonitor />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Feature Journey: Step-by-Step Experience */}
        <section className="py-5 my-5 position-relative overflow-hidden">
          <div className="container py-5">
            <div className="text-center mb-5 animate-fade-in-up">
              <div className="badge glass-dark rounded-pill px-4 py-2 text-primary fw-900 mb-3 tracking-widest shadow-sm">THE SMARTSHRINK JOURNEY</div>
              <h2 className="display-4 fw-900 text-dark mb-3 tracking-tight">Your Document's <span className="text-gradient">Evolution</span></h2>
              <p className="text-muted fw-medium max-w-md mx-auto fs-5">Experience a seamless flow from raw data to actionable intelligence in 4 easy steps.</p>
            </div>

            <div className="row g-4 mt-5">
              {[
                { step: "01", icon: <FiUploadCloud />, title: "Secure Ingestion", desc: "Upload PDFs, Images, or Text. Our system uses enterprise-grade encryption to secure your data instantly.", color: "primary" },
                { step: "02", icon: <FiCpu />, title: "Neural Deep-Scan", desc: "Our AI engine performs a semantic audit, extracting key entities and identifying optimization gaps.", color: "secondary" },
                { step: "03", icon: <FiZap />, title: "Smart Optimization", desc: "Compress file sizes without losing quality or get AI-generated improvements for your resume.", color: "success" },
                { step: "04", icon: <FiTarget />, title: "Actionable Results", desc: "Download optimized assets or get a comprehensive report to boost your professional success.", color: "info" }
              ].map((item, i) => (
                <div key={i} className="col-lg-3 col-md-6 animate-fade-in-up" style={{ animationDelay: `${i * 0.15}s` }}>
                  <div className="card-premium h-100 p-4 border-0 glass overflow-hidden hover-scale">
                    <div className={`badge bg-${item.color} bg-opacity-10 text-${item.color} rounded-pill px-3 py-1 fw-900 mb-4`}>STEP {item.step}</div>
                    <div className={`icon-box mb-4 bg-${item.color} shadow-sm`} style={{ width: '60px', height: '60px' }}>{item.icon}</div>
                    <h4 className="fw-900 text-dark mb-3">{item.title}</h4>
                    <p className="text-muted fw-medium mb-0 small lh-lg">{item.desc}</p>
                    <div className="position-absolute bottom-0 end-0 opacity-10 m-n3">
                      <span className="display-1 fw-900">{item.step}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Architect Studio Spotlight Section */}
        <section className="py-5 my-lg-5 position-relative overflow-hidden">
          <div className="container py-5">
            <div className="row align-items-center g-5 flex-lg-row-reverse">
              <div className="col-lg-6">
                <div className="position-relative">
                  <div className="card-premium p-1 glass overflow-hidden shadow-2xl animate-float" style={{ borderRadius: '3rem' }}>
                    <img
                      src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&q=80&w=1000"
                      className="img-fluid rounded-5 w-100"
                      alt="Engineering Blueprint"
                      style={{ height: '500px', objectFit: 'cover' }}
                    />
                    <div className="position-absolute bottom-0 start-0 m-4">
                      <div className="glass p-4 rounded-4 shadow-xl border-primary border-opacity-20 animate-pulse-glow">
                        <div className="d-flex align-items-center gap-3 mb-2">
                          <FiLayers className="text-primary" />
                          <div className="fw-900 text-dark">Mermaid Engine</div>
                        </div>
                        <div className="x-small text-muted fw-bold">Live Architecture Mapping</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-6 animate-fade-in-up">
                <div className="badge glass rounded-pill px-4 py-2 text-primary fw-900 mb-4 tracking-widest border border-primary border-opacity-10">
                  ENGINEERING EXCELLENCE
                </div>
                <h2 className="display-3 fw-900 text-dark mb-4 tracking-tight">
                  Architect <span className="text-gradient-premium">Intelligence</span>
                </h2>
                <p className="lead text-muted mb-5 fw-medium">
                  Professional-grade system design for everyone. Upload your documentation or project logic and watch our AI "shrink" it into optimized architectural blueprints.
                </p>

                <div className="row g-4 mb-5">
                  {[
                    { title: "Visual Flowcharts", icon: <FiLayout />, desc: "Auto-generate high-fidelity Mermaid.js diagrams from any input." },
                    { title: "Logic Optimization", icon: <FiZap />, desc: "Get a streamlined version of your system architecture." },
                    { title: "Bottleneck Audit", icon: <FiShield />, desc: "Identify performance and security risks before you build." }
                  ].map((item, i) => (
                    <div key={i} className="col-12">
                      <div className="d-flex gap-4 p-3 rounded-4 glass border-0 hover-scale cursor-pointer" onClick={() => navigate('/architect-studio')}>
                        <div className="icon-box mb-0 flex-shrink-0" style={{ width: '50px', height: '50px', borderRadius: '15px' }}>{item.icon}</div>
                        <div>
                          <div className="fw-900 text-dark mb-1">{item.title}</div>
                          <div className="small text-muted fw-medium">{item.desc}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <button onClick={() => navigate('/architect-studio')} className="btn-premium px-5 py-4 fs-4 shadow-2xl">
                  Open Architect Studio <FiCpu />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Student Success Path: What to do in this platform */}
        <section className="py-5 bg-dark position-relative overflow-hidden shadow-2xl" style={{ borderRadius: '4rem 4rem 0 0' }}>
          <div className="blob bg-primary" style={{ top: '10%', left: '10%', width: '600px', height: '600px', opacity: 0.1 }}></div>
          <div className="container py-5 position-relative z-10">
            <div className="row align-items-center g-5">
              <div className="col-lg-6 animate-fade-in-up">
                <div className="badge glass-dark rounded-pill px-4 py-2 text-primary fw-900 mb-4 shadow-sm border-0">FOR THE FUTURE LEADERS 🎓</div>
                <h2 className="display-3 fw-900 text-white mb-4 tracking-tight">The <span className="text-gradient-premium">Student Success</span> Blueprint</h2>
                <p className="text-light opacity-75 fs-4 fw-medium mb-5">
                  Turn your academic pressure into professional power. Here is exactly how to use SmartShrink to dominate your studies.
                </p>

                <div className="d-flex flex-column gap-4">
                  {[
                    { title: "Upload Course Material", text: "Drop your PDFs or textbook scans into Edu-Pulse to start the AI digestion process.", icon: <FiFileText />, path: "/edu-studio" },
                    { title: "Generate Knowledge Kits", text: "Get instant flashcards, quizzes, and summaries tailored to your syllabus.", icon: <FiZap />, path: "/edu-studio" },
                    { title: "Architect Your Projects", text: "Use Architect Studio to turn your project ideas into professional blueprints and diagrams.", icon: <FiCpu />, path: "/architect-studio" },
                    { title: "Ace Your Career Start", text: "Use Career-Sync to practice interviews and optimize your resume for your first job.", icon: <FiTrendingUp />, path: "/career-sync" }
                  ].map((step, i) => (
                    <div key={i} className="d-flex gap-4 align-items-start glass-dark p-4 rounded-4 hover-scale cursor-pointer border-0" onClick={() => navigate(step.path)}>
                      <div className="bg-primary bg-opacity-20 p-3 rounded-circle text-primary shadow-glow">
                        {step.icon}
                      </div>
                      <div>
                        <h5 className="text-white fw-900 mb-2">{step.title}</h5>
                        <p className="text-light opacity-50 mb-0 small fw-medium">{step.text}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <button onClick={() => navigate('/edu-studio')} className="btn-premium px-5 py-4 fs-4 mt-5 w-100 w-lg-auto">
                  Start Your Success Journey <FiArrowRight />
                </button>
              </div>
              <div className="col-lg-6">
                <div className="position-relative">
                  <div className="card-premium p-1 glass-dark overflow-hidden shadow-2xl animate-float" style={{ borderRadius: '3rem' }}>
                    <img
                      src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&q=80&w=1000"
                      className="img-fluid rounded-5 w-100"
                      alt="Students Studying"
                      style={{ minHeight: '500px', objectFit: 'cover', opacity: 0.8 }}
                    />
                    <div className="position-absolute inset-0 bg-gradient-to-t from-dark to-transparent opacity-60"></div>
                  </div>

                  {/* Overlay Success Metric */}
                  <div className="position-absolute bottom-0 start-50 translate-middle-x mb-n4 w-75">
                    <div className="glass p-4 rounded-4 shadow-2xl text-center border-primary border-opacity-25">
                      <div className="display-4 fw-900 text-dark mb-0">85%</div>
                      <div className="text-muted small fw-900 uppercase tracking-widest">Retention Increase</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section - Core Capabilities */}
        <section className="py-5 bg-white position-relative">
          <div className="container py-5">
            <div className="text-center mb-5 animate-fade-in-up">
              <div className="badge glass-dark rounded-pill px-4 py-2 text-primary fw-900 mb-3 tracking-widest shadow-sm">BEYOND THE BASICS</div>
              <h2 className="display-4 fw-900 text-dark mb-3 tracking-tight">Enterprise-Grade <span className="text-gradient">Capabilities</span></h2>
              <p className="text-muted fw-medium max-w-md mx-auto fs-5">Whether you are a student or a pro, our engine scales to your needs.</p>
            </div>

            <div className="row g-4 mt-4">
              {[
                { icon: <FiCpu />, title: "Neural Parsing", desc: "Deep parsing of document structure and content semantics using advanced LLMs." },
                { icon: <FiShield />, title: "Bank-Grade Security", desc: "Military-grade encryption for all your uploads with instant post-process deletion." },
                { icon: <FiZap />, title: "Instant Optimization", desc: "Get real-time feedback and compressed assets in milliseconds, not minutes." },
                { icon: <FiMonitor />, title: "Cross-Platform", desc: "Seamless experience across desktop, tablet, and mobile with cloud-sync capability." },
                { icon: <FiActivity />, title: "Growth Analytics", desc: "Track your document performance and ATS compatibility over time." },
                { icon: <FiLayers />, title: "Batch Processing", desc: "Optimize entire folders of documents with our high-throughput pipeline." }
              ].map((feature, i) => (
                <div key={i} className="col-md-6 col-lg-4 animate-fade-in-up" style={{ animationDelay: `${i * 0.1}s` }}>
                  <div className="feature-card">
                    <div className="icon-box">{feature.icon}</div>
                    <h4 className="fw-900 text-dark mb-3">{feature.title}</h4>
                    <p className="text-muted fw-medium mb-0">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* AI Analyzer Section */}
        <section id="analyzer-section" ref={analyzerRef} className="py-5 my-5 position-relative">
          <div className="text-center mb-5 animate-fade-in-up">
            <div className="badge glass rounded-pill px-4 py-2 text-primary fw-900 mb-3 tracking-wider shadow-sm border-primary border-opacity-25">
              SMART INTELLIGENCE ENGINE
            </div>
            <h2 className="display-4 fw-900 mb-3 tracking-tight">
              Launch Your <span className="text-gradient">Analysis</span>
            </h2>
            <p className="text-muted fs-5 fw-medium max-w-md mx-auto">Upload your document below and let our AI handle the rest.</p>
          </div>

          <div className="container py-5">
            <div className="row justify-content-center">
              <div className="col-xl-11">
                <div className="p-1 rounded-5" style={{ background: "var(--premium-gradient)", boxShadow: "0 25px 80px rgba(79,70,229,0.25)" }}>
                  <div className="bg-dark rounded-5 p-3 p-md-5 position-relative overflow-hidden" style={{ backdropFilter: "blur(20px)", minHeight: "500px" }}>
                    <div className="position-absolute top-0 end-0 opacity-10" style={{ transform: "translate(20%,-20%)" }}>
                      <FiActivity size={280} className="text-info" />
                    </div>

                    <div className="row g-4 position-relative">
                      {/* Left Upload Panel */}
                      <div className="col-lg-5">
                        <div className="mb-4">
                          <h2 className="text-white fw-900 mb-2">AI Resume Analyzer</h2>
                          <p className="text-light opacity-75 fw-medium">Upload your resume and get deep insights powered by neural intelligence.</p>
                        </div>

                        <div
                          className={`rounded-5 p-5 text-center border-2 h-100 d-flex flex-column justify-content-center transition-all hover-scale ${file ? "border-success bg-success bg-opacity-10" : "border-secondary border-dashed"}`}
                          style={{ cursor: "pointer" }}
                          onClick={() => document.getElementById("resume-upload")?.click()}
                        >
                          <div className="mb-4 animate-float">
                            {file ? <FiCheckCircle size={80} className="text-success" /> : <FiFileText size={80} className="text-primary opacity-50" />}
                          </div>
                          <h4 className="text-white fw-900">{file ? "Document Ready" : "Drop PDF Here"}</h4>
                          <p className="text-light opacity-50 fw-bold small">PDF ONLY • MAX 10MB</p>
                          {file && <div className="badge glass-dark mt-3 px-3 py-2 text-success fw-900 border-0">{file.name}</div>}
                          <input id="resume-upload" type="file" accept=".pdf" className="d-none" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                        </div>
                      </div>

                      {/* Right Analysis Panel */}
                      <div className="col-lg-7">
                        <div className="h-100 d-flex flex-column">
                          <div className="mb-4">
                            <div className="d-flex align-items-center gap-3 mb-3">
                              <div className="p-3 rounded-4 bg-primary bg-opacity-25 shadow-glow"><FiCpu className="text-primary" size={24} /></div>
                              <h4 className="text-white fw-900 mb-0">Intelligence Core</h4>
                            </div>
                            <p className="text-light opacity-75 lh-lg fw-medium">Our neural engine scans keywords, ATS compatibility, readability, and industry relevance to provide actionable career growth metrics.</p>
                          </div>

                          <div className="row g-3 mb-4">
                            {["ATS Scoring", "Keyword Delta", "Layout Audit", "Skill Mapping"].map((item) => (
                              <div className="col-6" key={item}>
                                <div className="glass-dark rounded-4 p-3 text-center border-0">
                                  <small className="text-info fw-900 tracking-wider uppercase">{item}</small>
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="mt-auto">
                            <button
                              onClick={handleAnalyze}
                              disabled={loading || !file}
                              className="btn-premium w-100 py-4 fs-4 shadow-2xl"
                            >
                              <div className="d-flex justify-content-center align-items-center gap-3">
                                {loading ? <FiLoader className="spin" /> : <FiSearch />}
                                {loading ? "Neural Crunching..." : "RUN ANALYSIS"}
                              </div>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Intelligence Report Results */}
          {analysisResult && (
            <div className="container mt-5 animate-fade-in-up">
              <div className="row justify-content-center">
                <div className="col-xl-11">
                  <div className="p-1 rounded-5 shadow-2xl" style={{ background: 'var(--premium-gradient)' }}>
                    <div className="card-premium glass border-0 overflow-hidden" style={{ borderRadius: '2rem' }}>
                      <div className="card-header bg-dark p-4 border-0 d-flex justify-content-between align-items-center">
                        <h4 className="text-white fw-900 mb-0 d-flex align-items-center gap-3">
                          <span className="bg-primary bg-opacity-20 p-2 rounded-3 text-primary d-flex shadow-glow"><FiMonitor size={22} /></span>
                          INTELLIGENCE REPORT
                        </h4>
                        <div className="badge glass-dark rounded-pill px-3 py-2 text-success small fw-900 border-0 shadow-sm">VERIFIED BY AI</div>
                      </div>
                      <div className="card-body p-4 p-md-5 bg-white position-relative">
                        <div className="analysis-container custom-scrollbar" style={{ maxHeight: "600px", overflowY: "auto", paddingRight: "15px" }}>
                          {parseAndHighlightText(analysisResult)}
                        </div>
                        <div className="position-absolute bottom-0 start-0 w-100" style={{ height: '60px', background: 'linear-gradient(to top, rgba(255,255,255,1), transparent)', pointerEvents: 'none' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* How It Works Section */}
        <section className="py-5 my-5">
          <div className="container py-5">
            <div className="row justify-content-center text-center mb-5">
              <div className="col-lg-8">
                <h2 className="display-4 fw-900 text-dark mb-4 tracking-tight">How it <span className="text-gradient">Works</span></h2>
                <p className="text-muted fs-5 fw-medium">Simplicity meets powerful computation. Here is how we transform your documents.</p>
              </div>
            </div>

            <div className="row g-5 mt-4">
              {[
                { title: "Upload", desc: "Select your document and let our secure gateway handle the initial neural parsing.", icon: <FiUploadCloud /> },
                { title: "Analyze", desc: "Our AI engine executes thousands of checks to identify gaps and opportunities.", icon: <FiCpu /> },
                { title: "Succeed", desc: "Receive a comprehensive report and optimized assets to boost your profile.", icon: <FiAward /> }
              ].map((step, i) => (
                <div key={i} className="col-md-4 position-relative animate-fade-in-up" style={{ animationDelay: `${i * 0.2}s` }}>
                  <div className="text-center px-4">
                    <div className="step-number">{i + 1}</div>
                    <div className="icon-box mx-auto mb-4" style={{ width: '80px', height: '80px', borderRadius: '24px' }}>
                      {step.icon}
                    </div>
                    <h4 className="fw-900 text-dark mb-3">{step.title}</h4>
                    <p className="text-muted fw-medium">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-dark py-5 mt-5 position-relative z-10">
        <div className="container">
          <div className="row g-4 align-items-center">
            <div className="col-md-6 text-center text-md-start">
              <div className="d-flex align-items-center justify-content-center justify-content-md-start gap-3 text-white mb-3">
                <div className="bg-primary p-2 rounded-3 shadow-sm"><FiScissors size={28} className="text-white" /></div>
                <span className="fs-3 fw-900 tracking-tight">SmartShrink</span>
              </div>
              <p className="text-light opacity-50 max-w-sm mx-auto mx-md-0 fw-medium">The world's most advanced document intelligence and optimization platform.</p>
            </div>
            <div className="col-md-6 text-center text-md-end">
              <div className="d-flex justify-content-center justify-content-md-end gap-4">
                <a href="#" className="text-white opacity-50 text-decoration-none hover-text-primary transition-all fw-bold hover-scale">Privacy</a>
                <a href="#" className="text-white opacity-50 text-decoration-none hover-text-primary transition-all fw-bold hover-scale">Terms</a>
                <a href="#" className="text-white opacity-50 text-decoration-none hover-text-primary transition-all fw-bold hover-scale">Contact</a>
              </div>
            </div>
          </div>
          <div className="border-top border-white border-opacity-10 mt-5 pt-4 text-center">
            <p className="text-light opacity-25 small mb-0 fw-bold tracking-wider">© 2026 SMARTSHRINK GLOBAL. ENGINEERED FOR PERFECTION.</p>
          </div>
        </div>
      </footer>

      <style>{`
        .animate-spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        
        .transform-perspective {
          transform: perspective(1000px) rotateY(-5deg) rotateX(5deg);
          transition: transform 0.6s cubic-bezier(0.25, 1, 0.5, 1);
        }
        .transform-perspective:hover {
          transform: perspective(1000px) rotateY(0deg) rotateX(0deg) scale(1.02);
        }
        
        .analysis-container h4 {
           letter-spacing: -0.5px;
           color: var(--primary);
        }
        
        .analysis-container p {
           line-height: 1.8;
           color: var(--text-main);
           margin-bottom: 1.2rem;
           font-weight: 500;
        }
        
        .text-primary { color: var(--primary) !important; }
        .text-secondary { color: var(--secondary) !important; }
        .text-success { color: var(--accent) !important; }
        .text-info { color: #0ea5e9 !important; }
        .bg-primary { background-color: var(--primary) !important; }
        .bg-secondary { background-color: var(--secondary) !important; }
        .bg-success { background-color: var(--accent) !important; }
        .bg-info { background-color: #0ea5e9 !important; }
        
        .hover-text-primary:hover { color: var(--primary) !important; }
        .bg-primary-gradient { background: var(--primary-gradient); }
        
        @media (max-width: 991px) {
           .transform-perspective { transform: none !important; }
           .hero-title { font-size: 3.5rem; }
        }

        .x-small { font-size: 0.75rem; }
        .uppercase { text-transform: uppercase; }
      `}</style>
    </div>
  );
}